import React, { useState } from 'react';
import TurnstileWidget from '../components/TurnstileWidget';
import { API_BASE_URL, TURNSTILE_SITE_KEY } from '../config';

const FEATURES = [
  {
    title: 'Fácil de usar',
    description: 'Selecciona o arrastra un archivo PDF. En segundos descarga tu PDF VUCEM.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"></path>
      </svg>
    ),
  },
  {
    title: 'Calidad',
    description: 'Comprime y conserva la calidad a 300 DPI y escala de grises.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.9 4.9L5 9l4.9 1.9L12 16l1.9-5.1L19 9l-4.9-1.1z"></path>
      </svg>
    ),
  },
  {
    title: 'Conversión segura',
    description: 'No guardamos tu archivo. Se procesa y se descarta al terminar la conversión.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    ),
  },
];

const ConvertidorPage = () => {
  const [file, setFile] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | uploading | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const needsTurnstile = Boolean(TURNSTILE_SITE_KEY);

  const pickFile = (selected) => {
    if (!selected) return;
    setFile(selected);
    setStatus('idle');
    setErrorMessage('');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    pickFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      setStatus('error');
      setErrorMessage('Selecciona un archivo PDF primero.');
      return;
    }
    if (needsTurnstile && !turnstileToken) {
      setStatus('error');
      setErrorMessage('Completa la verificación de seguridad.');
      return;
    }

    setStatus('uploading');
    setErrorMessage('');

    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('turnstile_token', turnstileToken);

    try {
      const response = await fetch(`${API_BASE_URL}/api/vucem-publico/convertir/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(data.error || 'No se pudo convertir el archivo.');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const stem = file.name.replace(/\.pdf$/i, '');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${stem}_VUCEM.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage('No se pudo contactar al servidor de conversión. Intenta de nuevo.');
    } finally {
      // Turnstile tokens are single-use: get a fresh one for the next conversion.
      setTurnstileToken('');
      setTurnstileKey((key) => key + 1);
    }
  };

  return (
    <main>
      <section className="w-full py-12 md:py-20 lg:py-24 bg-gray-50 dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-3xl mx-auto">
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-10">
            <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-gray-900 dark:text-gray-100">
              Convertidor PDF VUCEM
            </h1>
            <p className="max-w-[700px] text-gray-600 md:text-xl/relaxed dark:text-gray-400">
              Convierte tu PDF al formato requerido por VUCEM: 300 DPI y escala de grises.
            </p>
            <a
              href="/como-convertir-pdf-vucem"
              className="text-sm font-medium text-indigo-700 hover:underline dark:text-teal-400"
            >
              ¿Primera vez? Consulta los requisitos y el paso a paso
            </a>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-gray-300 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900"
          >
            <label
              htmlFor="archivo-pdf"
              onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-indigo-700 bg-indigo-50 dark:border-teal-500 dark:bg-teal-950/30'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            >
              <input
                id="archivo-pdf"
                type="file"
                accept="application/pdf"
                className="sr-only"
                onChange={(event) => pickFile(event.target.files?.[0])}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {file ? file.name : 'Selecciona o arrastra un archivo PDF'}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-500">Máximo 10 MB</span>
            </label>

            {needsTurnstile && <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />}

            {status === 'error' && (
              <p className="mt-4 text-sm font-medium text-red-600 dark:text-red-400">{errorMessage}</p>
            )}
            {status === 'success' && (
              <p className="mt-4 text-sm font-medium text-green-600 dark:text-green-400">
                Conversión lista. La descarga debería comenzar automáticamente.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'uploading'}
              className="mt-6 inline-flex h-10 w-full items-center justify-center rounded-md bg-indigo-700 px-8 text-sm font-medium text-white shadow transition-colors hover:bg-indigo-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-teal-500 dark:text-gray-900 dark:hover:bg-teal-600 dark:focus-visible:ring-teal-300"
            >
              {status === 'uploading' ? 'Convirtiendo…' : 'Convertir'}
            </button>
          </form>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="grid gap-2 p-6 rounded-lg border border-gray-300 bg-white shadow-sm transition-all hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-800">{feature.icon}</div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{feature.title}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center gap-4 rounded-lg border border-indigo-200 bg-white p-6 text-center shadow-sm md:p-8 dark:border-indigo-900 dark:bg-gray-900">
            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-800 dark:text-indigo-100">
              Nuestro software
            </span>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              ¿Conviertes documentos para VUCEM todos los días?
            </h2>
            <p className="max-w-xl text-gray-600 dark:text-gray-400">
              Este convertidor es parte de EC Traffic, nuestra plataforma de comercio exterior. Además de digitalizar tus documentos, los transmite a VUCEM, obtiene tus e-documents y mantiene en orden el expediente de cada pedimento.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href="/#plataforma"
                className="inline-flex h-10 items-center justify-center rounded-md bg-indigo-700 px-6 text-sm font-medium text-white shadow transition-colors hover:bg-indigo-800 dark:bg-teal-500 dark:text-gray-900 dark:hover:bg-teal-600"
              >
                Conoce EC Traffic
              </a>
              <a
                href="/#contacto"
                className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 px-6 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Solicitar una demostración
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ConvertidorPage;
